import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Backdrop,
  CircularProgress,
  DialogActions,
} from "@mui/material";
import styled from "styled-components";
import api from "../../api/config";
import { useNotification } from "../../context/NotificationContext";
import StyledTextField from "./StyledTextField";
import { AddClient, UpdateClient } from "../../api/Client";
import MapComponent from "./MapComponent";

const Row = styled.div`
  display: flex;
  gap: 16px;
`;

const Container = styled.div`
  display: flex;
  gap: 20px;
  height: 500px;
`;

const FormContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  overflow-y: auto;
`;

const MapContainer = styled.div`
  flex: 1;
  height: 100%;
  border-radius: 10px;
  overflow: hidden;
`;

const Header = styled(DialogTitle)`
  background-color: #28929c;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
`;

const Content = styled(DialogContent)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px !important;
  background-color: #2b3342;
  color: white;
`;

const Footer = styled(DialogActions)`
  background-color: #28929c;
  padding: 10px;
`;

export default function AddClientModal({
  open,
  onClose,
  onSaveSuccess = () => {},
  isEdit = false,
  selectedClient = {},
}) {
  const [loading, setLoading] = useState(false);
  const { showNotification } = useNotification();
  const [errors, setErrors] = useState({});

  const initialClientData = {
    npi_name: "",
    npi_number: "",
    address: "",
    state: "",
    country: "",
    zipcode: "",
    logo_url: "",
    lifeimage_url: "",
    latitude: "",
    longitude: "",
    website: "",
    phone: "",
    fax: "",
    email: "",
    powershare_url: "",
    subdomain: "",
    notes: "",
  };

  const [clientData, setClientData] = useState(initialClientData);

  useEffect(() => {
    if (isEdit && selectedClient) {
      setClientData(
        isEdit && selectedClient
          ? { ...initialClientData, ...selectedClient }
          : initialClientData
      );
    }
  }, [isEdit, selectedClient]);

  const validateLatLon = (name, value) => {
    let error = "";
    if (name === "latitude" && (value < -90 || value > 90)) {
      error = "Latitude must be between -90 and 90.";
    }
    if (name === "longitude" && (value < -180 || value > 180)) {
      error = "Longitude must be between -180 and 180.";
    }
    return error;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let validatedValue = value;

    if (name === "latitude" || name === "longitude") {
      validatedValue = parseFloat(value) || "";
    }

    setClientData((prevData) => ({ ...prevData, [name]: validatedValue }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: validateLatLon(name, validatedValue),
    }));
  };

  const handleSave = async () => {
    if (errors.latitude || errors.longitude) {
      showNotification("Please correct the errors before saving.", "error");
      return;
    }

    setLoading(true);
    try {
      if (isEdit) {
        console.log("Update client:", clientData);
        await api.put(`${UpdateClient}/${selectedClient.uuid}`, clientData);
        showNotification("Client Updated Successfully", "success");
      } else {
        console.log("Create client:", clientData);
        await api.post(AddClient, clientData);
        showNotification("Client Created Successfully", "success");
      }

      onSaveSuccess();
      handleClose();
    } catch (error) {
      console.error("Error saving client:", error);
      showNotification("Error saving client", "error");
    }
    setLoading(false);
  };

  const resetData = () => {
    setClientData(initialClientData);
    setErrors({});
  };

  const handleClose = () => {
    resetData();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <Header>{isEdit ? "Edit Client" : "Add Client"}</Header>

      <Content>
        <Container>
          <FormContainer>
            <Row>
              <StyledTextField
                label="NPI Name"
                name="npi_name"
                value={clientData.npi_name}
                onChange={handleInputChange}
                fullWidth
              />
              <StyledTextField
                label="NPI Number"
                name="npi_number"
                value={clientData.npi_number}
                onChange={handleInputChange}
                fullWidth
              />
            </Row>
            <StyledTextField
              label="Address"
              name="address"
              value={clientData.address}
              onChange={handleInputChange}
              fullWidth
            />
            <Row>
              <StyledTextField
                label="State"
                name="state"
                value={clientData.state}
                onChange={handleInputChange}
                fullWidth
              />
              <StyledTextField
                label="Country"
                name="country"
                value={clientData.country}
                onChange={handleInputChange}
                fullWidth
              />
              <StyledTextField
                label="Zip Code"
                name="zipcode"
                value={clientData.zipcode}
                onChange={handleInputChange}
                fullWidth
              />
            </Row>
            <Row>
              <StyledTextField
                label="Logo URL"
                name="logo_url"
                value={clientData.logo_url}
                onChange={handleInputChange}
                fullWidth
              />
              <StyledTextField
                label="Life Image URL"
                name="lifeimage_url"
                value={clientData.lifeimage_url}
                onChange={handleInputChange}
                fullWidth
              />
            </Row>
            <Row>
              <StyledTextField
                label="Latitude"
                name="latitude"
                value={clientData.latitude}
                onChange={handleInputChange}
                fullWidth
                error={Boolean(errors.latitude)}
                helperText={errors.latitude || ""}
              />
              <StyledTextField
                label="Longitude"
                name="longitude"
                value={clientData.longitude}
                onChange={handleInputChange}
                fullWidth
                error={Boolean(errors.longitude)}
                helperText={errors.longitude || ""}
              />
            </Row>
            <Row>
              <StyledTextField
                label="Website URL"
                name="website"
                value={clientData.website}
                onChange={handleInputChange}
                fullWidth
              />
              <StyledTextField
                label="Phone"
                name="phone"
                value={clientData.phone}
                onChange={handleInputChange}
                fullWidth
              />
            </Row>
            <Row>
              <StyledTextField
                label="Fax"
                name="fax"
                value={clientData.fax}
                onChange={handleInputChange}
                fullWidth
              />
              <StyledTextField
                label="Email"
                name="email"
                value={clientData.email}
                onChange={handleInputChange}
                fullWidth
              />
            </Row>
            <Row>
              <StyledTextField
                label="Powershare URL"
                name="powershare_url"
                value={clientData.powershare_url}
                onChange={handleInputChange}
                fullWidth
              />
              <StyledTextField
                label="Subdomain"
                name="subdomain"
                value={clientData.subdomain}
                onChange={handleInputChange}
                fullWidth
              />
            </Row>
            <StyledTextField
              label="Notes"
              name="notes"
              value={clientData.notes}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={3}
            />
          </FormContainer>
          <MapContainer>
            <MapComponent
              latitude={errors.latitude ? null : clientData.latitude}
              longitude={errors.longitude ? null : clientData.longitude}
              setLatitude={(lat) =>
                handleInputChange({ target: { name: "latitude", value: lat } })
              }
              setLongitude={(lng) =>
                handleInputChange({ target: { name: "longitude", value: lng } })
              }
            />
          </MapContainer>
        </Container>
      </Content>

      <Footer>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave}>
          {isEdit ? "Update Client" : "Save Client"}
        </Button>
      </Footer>

      <Backdrop open={loading} style={{ zIndex: 1300, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
}
